/* ==========================================================================
   ARYOS — theme.js
   Baseline product-page enhancements only:
   - Quantity selector (+/- buttons, respects min/step/max)
   - Variant-change controller (matches selected options to a real variant,
     updates price/media/add-to-cart state, updates the URL, dispatches a
     variant:change event)

   Everything here is scoped to the nearest [data-section-type="main-product"]
   root so multiple product sections can coexist in the Theme Editor without
   cross-contamination. No AJAX cart, no inventory polling, no external
   libraries.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__aryosThemeJsInit) return;
  window.__aryosThemeJsInit = true;

  /* ---------------------------------------------------------------------
     Variant payload — reconstructs per-variant objects

     main-product.liquid now sends a "structure of arrays" payload (one
     array per field, index-aligned to product.variants) rather than an
     array of per-variant objects, since that's what Liquid can build
     safely without manual JSON assembly. This zips those parallel arrays
     back into ordinary { id, available, option1, ... } objects once, so
     the rest of this file reads plain variant.field values exactly as if
     Liquid had sent an array of objects directly.

     Prices arrive pre-formatted (formatted_price, formatted_compare_at_price,
     formatted_unit_price) via Shopify's own money filter, already correct
     for the visitor's active Shopify Market — no client-side currency
     formatting or hardcoded symbol is needed here at all.
     --------------------------------------------------------------------- */
  function zipVariantPayload(payload) {
    var count = payload.ids.length;
    var variants = [];
    for (var i = 0; i < count; i++) {
      variants.push({
        id: payload.ids[i],
        available: payload.available[i],
        option1: payload.option1[i],
        option2: payload.option2[i],
        option3: payload.option3[i],
        price: payload.price[i],
        compare_at_price: payload.compare_at_price[i],
        unit_price: payload.unit_price[i],
        unit_price_measurement: payload.unit_price_measurement[i],
        featured_media_id: payload.featured_media_id[i],
        formatted_price: payload.formatted_price[i],
        formatted_compare_at_price: payload.formatted_compare_at_price[i],
        formatted_unit_price: payload.formatted_unit_price[i]
      });
    }
    return variants;
  }

  /* ---------------------------------------------------------------------
     Enable the hidden variant input

     product-form.liquid disables this input by default whenever the
     no-JS <select name="id"> fallback exists, so that with JavaScript
     off, the fallback select is the only successful "id" control. Once
     this script runs, JavaScript is confirmed present, so the hidden
     input is re-enabled and becomes the JS-maintained source of truth;
     the <noscript> fallback is never present in the DOM in this case
     anyway, so there is no risk of two competing "id" controls.
     --------------------------------------------------------------------- */
  function enableVariantIdInput(root) {
    var input = root.querySelector('[data-variant-id-input]');
    if (input && input.disabled) {
      input.disabled = false;
    }
  }

  /* ---------------------------------------------------------------------
     Shared body scroll lock

     Exposed on window so header.liquid's own inline <script> (which
     currently sets document.body.style.overflow directly for its nav and
     cart drawers) can call the same lock/unlock pair. Reference-counted so
     that if two drawers are ever open at once (e.g. the filter drawer and
     the cart drawer), closing one never unlocks the page out from under
     the other. Captures whatever inline overflow value already existed
     before the first lock and restores exactly that value once the last
     lock is released, rather than assuming it was empty.
     --------------------------------------------------------------------- */
  if (!window.AryosScrollLock) {
    (function () {
      var lockCount = 0;
      var savedOverflow = null;

      window.AryosScrollLock = {
        lock: function () {
          if (lockCount === 0) {
            savedOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
          }
          lockCount++;
        },
        unlock: function () {
          if (lockCount === 0) return;
          lockCount--;
          if (lockCount === 0) {
            document.body.style.overflow = savedOverflow || '';
            savedOverflow = null;
          }
        }
      };
    })();
  }

  /* ---------------------------------------------------------------------
     Mobile filter drawer

     The filter form (snippets/collection-filters.liquid) is a single DOM
     instance shared by the desktop sidebar and the mobile drawer — CSS
     alone decides which it looks like. This controller only adds the
     `.js-enhanced` class (which is what lets CSS turn it into an
     off-canvas drawer on mobile at all) and manages open/close, focus,
     Escape, and body-scroll locking. Without this script, the same form
     simply renders inline on every breakpoint — filtering never depends
     on this JavaScript.

     Supports Shopify Theme Editor lifecycle: re-initializing a section
     (shopify:section:load) tears down any previous instance for that
     section first, and removing a section (shopify:section:unload) closes
     its drawer (releasing any scroll lock it held) and removes all
     listeners, so nothing is left stale or double-registered.

     Note: the focus-trap utility below duplicates a small (~20 line)
     equivalent that also exists inside header.liquid's own inline
     <script>. That script is self-contained within its own closure and
     isn't something this file can import from, so re-implementing the
     same small utility here was the pragmatic option rather than
     restructuring the header's script into a shared global just for this.
     --------------------------------------------------------------------- */
  function getFocusableIn(container) {
    return Array.prototype.slice
      .call(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        // Excludes anything currently not rendered — in particular,
        // controls inside a closed native <details> — so the trap is
        // always correct even as filter groups open/closed after the
        // drawer itself was opened.
        return el.offsetParent !== null;
      });
  }

  function trapFocusWithin(container, onClose) {
    var initial = getFocusableIn(container);
    if (initial.length) initial[0].focus();

    function keyHandler(e) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      var focusable = getFocusableIn(container);
      if (!focusable.length) {
        e.preventDefault();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', keyHandler);

    return function cleanup() {
      container.removeEventListener('keydown', keyHandler);
    };
  }

  var filterDrawerTeardowns = {};

  function initFilterDrawer(root) {
    var sectionId = root.getAttribute('data-section-id');

    // Re-initializing (Theme Editor shopify:section:load) — tear down any
    // previous instance for this exact section first so listeners and
    // scroll-lock state never double up.
    if (sectionId && filterDrawerTeardowns[sectionId]) {
      filterDrawerTeardowns[sectionId]();
    }

    var drawer = root.querySelector('[data-filter-drawer]');
    var trigger = root.querySelector('[data-filter-drawer-open]');
    if (!drawer || !trigger) return;

    // Presence of this script confirms JS is available — CSS only turns
    // the shared filter form into an off-canvas mobile drawer once this
    // class is present; otherwise it stays inline at every breakpoint.
    root.classList.add('js-enhanced');

    var closeEls = root.querySelectorAll('[data-filter-close]');
    var panel = drawer.querySelector('[data-filter-form]') || drawer;
    var cleanupFocusTrap = null;
    var lastFocused = null;
    var lockHeld = false;

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      cleanupFocusTrap = trapFocusWithin(panel, close);
      window.AryosScrollLock.lock();
      lockHeld = true;
    }

    function close() {
      if (!isOpen()) return;
      drawer.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (cleanupFocusTrap) {
        cleanupFocusTrap();
        cleanupFocusTrap = null;
      }
      if (lockHeld) {
        window.AryosScrollLock.unlock();
        lockHeld = false;
      }
      if (lastFocused) lastFocused.focus();
    }

    function onTriggerClick() {
      if (isOpen()) {
        close();
      } else {
        open();
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' && isOpen()) close();
    }

    trigger.addEventListener('click', onTriggerClick);
    closeEls.forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', onKeydown);

    filterDrawerTeardowns[sectionId] = function teardown() {
      // Ensures any held scroll lock and focus-trap listener are released
      // even if the section is removed while its drawer was still open.
      close();
      trigger.removeEventListener('click', onTriggerClick);
      closeEls.forEach(function (el) {
        el.removeEventListener('click', close);
      });
      document.removeEventListener('keydown', onKeydown);
      delete filterDrawerTeardowns[sectionId];
    };
  }

  /* ---------------------------------------------------------------------
     Shopify Theme Editor lifecycle (collection sections only)

     Registered once, globally — not inside initFilterDrawer — so re-
     rendering or removing a section never accumulates duplicate document-
     level listeners.
     --------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (event) {
    var loadedRoot =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-section-type="main-collection"]')
        : null;
    if (loadedRoot) {
      initCollectionSorting(loadedRoot);
      initFilterDrawer(loadedRoot);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var unloadedRoot =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-section-type="main-collection"]')
        : null;
    var sectionId = unloadedRoot && unloadedRoot.getAttribute('data-section-id');
    if (sectionId && filterDrawerTeardowns[sectionId]) {
      filterDrawerTeardowns[sectionId]();
    }
  });

  /* ---------------------------------------------------------------------
     Cart — AJAX drawer, add-to-cart, quantity/remove

     Architecture: every cart mutation (add/change) requests
     `sections=cart-drawer,cart-icon-bubble` from Shopify's native
     /cart/add.js and /cart/change.js endpoints. Both are real sections
     (see sections/cart-drawer.liquid and sections/cart-icon-bubble.liquid
     for why snippets can't be used here) rendered fully server-side —
     this file never builds cart HTML itself, only swaps in Shopify's own
     rendered markup via DOMParser, exactly like the predictive-search
     controller below.

     Scoping: quantity +/- and remove links are only AJAX-intercepted
     when they're inside [data-cart-drawer]. The cart PAGE (main-cart.liquid)
     reuses the identical cart-item markup but submits its native
     {% form 'cart' %} normally (full reload) — deliberately not AJAX-
     enhanced in this stage, since doing so correctly would mean either a
     second client-side money-formatting problem (the base /cart/change.js
     JSON response has no preformatted strings) or fetching a section for
     the page's own dynamic section id, which template-driven sections
     don't expose as a fixed, predictable name the way cart-drawer/
     cart-icon-bubble do. The cart page is fully functional either way —
     just via a normal page reload instead of a no-reload update.
     --------------------------------------------------------------------- */

  var cartDrawerTeardown = null;
  var cartBusyCount = 0;

  /* ---------------------------------------------------------------------
     Global cart mutation queue

     Every cart write — add, change, remove, and any future update —
     shares exactly one FIFO queue. Only one request is ever in flight
     against Shopify's cart at a time, regardless of which line or which
     operation it is. This is what actually prevents overlap: two
     different lines changing at once, an add racing a change, a remove
     racing an update, or two product forms submitting concurrently all
     used to be independent, unordered fetches. Now they're all just
     tasks in one queue, so:
       - they reach Shopify in the order they were enqueued
       - a task's response is fully applied before the next task starts
       - by construction, no older response can ever be applied after a
         newer one, since the newer one can't start until the older one
         (including its response handling) has finished
     Aborting is deliberately not used for cancellation: an aborted fetch
     only stops the browser from waiting on the response — it doesn't
     guarantee Shopify's server hasn't already processed that request.
     Serializing avoids that ambiguity entirely.
     --------------------------------------------------------------------- */
  var cartMutationQueue = [];
  var cartMutationRunning = false;

  // Incremented every time the cart drawer's section is torn down
  // (Theme Editor shopify:section:unload). A request already in flight at
  // that moment cannot be un-sent, and cartMutationRunning = false alone
  // doesn't stop its response from arriving afterward — if that response
  // handler blindly re-queried the DOM, it could find a *freshly reloaded*
  // section instance (shopify:section:load having since fired) and apply
  // stale mutation output on top of it. Each mutation captures the
  // generation it started in and checks, right before applying anything,
  // that the generation hasn't moved on — if it has, the response is
  // discarded as stale rather than applied.
  var cartGeneration = 0;

  function enqueueCartMutation(task) {
    return new Promise(function (resolve, reject) {
      cartMutationQueue.push({ task: task, resolve: resolve, reject: reject });
      processCartMutationQueue();
    });
  }

  function processCartMutationQueue() {
    if (cartMutationRunning) return;
    var next = cartMutationQueue.shift();
    if (!next) return;

    cartMutationRunning = true;
    setDrawerBusy(true);

    next
      .task()
      .then(next.resolve, next.reject)
      .finally(function () {
        cartMutationRunning = false;
        setDrawerBusy(false);
        // The queue cannot get stuck: whether the task resolved, rejected,
        // or threw, the next queued task (if any) always gets a turn.
        processCartMutationQueue();
      });
  }

  // Per-line quantity coalescing, layered on top of the global queue: a
  // line only ever has one mutation task in the queue/running at a time.
  // Further changes to that same line while its task is queued or running
  // just update the desired value; the task always reads the *latest*
  // desired value at the moment it actually executes (not the value from
  // whenever it was first requested), and re-enqueues itself once more if
  // the desired value changed again during its own execution. This still
  // fully respects the global queue — a coalesced line change is just one
  // more task competing for the same single FIFO slot as every add,
  // remove, and other line's change.
  var cartLineDesired = {};
  var cartLineQueued = {};

  function parseSectionHtml(html, sectionId) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('shopify-section-' + sectionId);
  }

  function setDrawerBusy(isBusy) {
    cartBusyCount = Math.max(0, cartBusyCount + (isBusy ? 1 : -1));
    var panel = document.querySelector('[data-cart-drawer-panel]');
    if (panel) panel.setAttribute('aria-busy', cartBusyCount > 0 ? 'true' : 'false');
  }

  function setLineBusy(key, isBusy) {
    var lineEl = document.querySelector('[data-cart-item][data-line-key="' + key + '"]');
    if (!lineEl) return;
    lineEl.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    lineEl.classList.toggle('is-loading', isBusy);
    lineEl.querySelectorAll('button, input, a[data-cart-remove]').forEach(function (el) {
      if (isBusy) {
        el.setAttribute('disabled', 'disabled');
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('disabled');
        el.removeAttribute('aria-disabled');
      }
    });
  }

  function applyCartSections(sectionsData) {
    if (!sectionsData) return;

    var existingDrawer = document.querySelector('[data-cart-drawer]');
    var drawerWasOpen = !!(existingDrawer && existingDrawer.classList.contains('is-open'));

    var existingNote = document.querySelector('[data-cart-note]');
    var preservedNote = existingNote ? existingNote.value : null;

    Object.keys(sectionsData).forEach(function (sectionId) {
      var liveEl = document.getElementById('shopify-section-' + sectionId);
      if (!liveEl) return;
      var html = sectionsData[sectionId];
      if (!html) return; // a section value of null/empty is skipped safely
      var returnedSection = parseSectionHtml(html, sectionId);
      if (!returnedSection) return;
      liveEl.innerHTML = returnedSection.innerHTML;
    });

    // The drawer and trigger button DOM nodes were just replaced — every
    // listener bound to the old nodes is gone, so re-bind against the new
    // ones. initCartDrawer() tears down its own previous instance first.
    // The fresh server-rendered drawer always comes back closed (`hidden`
    // is part of its static markup; `is-open` is JS-only state that never
    // survives a full innerHTML replacement) — if it was open before this
    // swap, reopen it now so an in-progress quantity edit never appears to
    // silently close the drawer out from under the shopper.
    initCartDrawer();
    if (drawerWasOpen && window.AryosCartDrawer) {
      window.AryosCartDrawer.open();
    }

    // Cart notes aren't sent with quantity-change requests (Shopify's
    // /cart/change.js only takes id + quantity), so a note the shopper is
    // mid-typing would otherwise be silently overwritten by the freshly-
    // rendered (still-server-side) value on every refresh. Restore it.
    var newNote = document.querySelector('[data-cart-note]');
    if (newNote && preservedNote !== null && preservedNote !== newNote.value) {
      newNote.value = preservedNote;
    }
  }

  function setCartStatus(text) {
    var statusEl = document.querySelector('[data-cart-status]');
    if (statusEl) statusEl.textContent = text;
  }

  function cartRequest(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) {
        return response
          .json()
          .catch(function () {
            throw new Error('cart request failed');
          })
          .then(function (data) {
            var err = new Error((data && data.description) || 'cart request failed');
            err.cartError = data;
            throw err;
          });
      }
      return response.json();
    });
  }

  /* ---------------------------------------------------------------------
     Resyncs the drawer/bubble to Shopify's actual current cart state
     after a failed mutation.

     /cart.js is a JSON data endpoint, not an HTML-rendering endpoint —
     it does not participate in Shopify's Section Rendering API at all,
     with or without a `sections` parameter. Bundled `sections=` support
     specifically belongs to the cart *mutation* endpoints (cart/add,
     cart/change, cart/update, cart/clear); it isn't a general-purpose
     refresh mechanism.

     For a standalone refresh with no mutation to attach it to, this uses
     the real Section Rendering API against a locale-aware storefront URL
     instead — routes.root_url, rendered by Liquid and exposed via
     data-cart-resync-url on the (never-replaced) header element. The
     final request looks like:

       {routes.root_url}?sections=cart-drawer,cart-icon-bubble

     e.g. against a storefront root of "/" this is "/?sections=cart-drawer,cart-icon-bubble";
     against a locale-prefixed root such as "/fr" it becomes
     "/fr?sections=cart-drawer,cart-icon-bubble" automatically, since
     routes.root_url is already locale-aware — nothing is hardcoded here.
     --------------------------------------------------------------------- */
  function resyncCartUI() {
    var headerEl = document.querySelector('[data-header]');
    var rootUrl = headerEl ? headerEl.getAttribute('data-cart-resync-url') : null;
    if (!rootUrl) return;

    var requestGeneration = cartGeneration;
    var separator = rootUrl.indexOf('?') > -1 ? '&' : '?';
    var resyncUrl = rootUrl + separator + 'sections=cart-drawer,cart-icon-bubble';

    fetch(resyncUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('resync failed');
        return response.json();
      })
      .then(function (sectionsData) {
        if (requestGeneration !== cartGeneration) return; // stale — section torn down/reloaded since this resync started
        // If a newer mutation has since started (e.g. the shopper retried
        // right after the failure), let that mutation's own response be
        // the source of truth instead — this best-effort resync would
        // otherwise risk applying an older snapshot on top of it.
        if (cartMutationRunning || cartMutationQueue.length > 0) return;
        applyCartSections(sectionsData);
      })
      .catch(function () {
        // Best-effort only. If even this fails, the UI keeps its last
        // known state and the visible error message (set by the caller)
        // stands as the concise retry prompt — never a second failure
        // mode stacked on top, never alert().
      });
  }

  function performChangeRequest(key, quantity) {
    setCartStatus('');
    setLineBusy(key, true);
    var requestGeneration = cartGeneration;
    var headerEl = document.querySelector('[data-header]');
    var changeUrl = (headerEl && headerEl.getAttribute('data-cart-change-url')) || '/cart/change.js';
    return cartRequest(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (cart) {
        if (requestGeneration !== cartGeneration) return; // stale — section was torn down/reloaded since this started
        applyCartSections(cart.sections);
      })
      .catch(function (err) {
        if (requestGeneration !== cartGeneration) return;
        // A quantity that exceeds availability, or a line that no longer
        // exists, surfaces Shopify's own message here — never a generic
        // alert(). The drawer is resynced to real server state rather
        // than assumed unchanged.
        setCartStatus((err && err.message) || '');
        resyncCartUI();
      })
      .finally(function () {
        setLineBusy(key, false);
      });
  }

  function changeCartLine(key, quantity) {
    cartLineDesired[key] = quantity;

    // A task for this line is already queued or running — it will read
    // the latest desired value (see below), so nothing more to do here.
    // This is the coalescing: rapid clicks only ever update the target
    // value, never stack up extra queue entries.
    if (cartLineQueued[key]) return;

    cartLineQueued[key] = true;

    enqueueCartMutation(function () {
      var quantityAtStart = cartLineDesired[key];
      return performChangeRequest(key, quantityAtStart).finally(function () {
        cartLineQueued[key] = false;
        // If more changes arrived for this line while this request was
        // queued or in flight, the desired value has since moved on —
        // fire exactly one more request so the server ultimately receives
        // the user's true latest intended quantity.
        if (cartLineDesired[key] !== quantityAtStart) {
          changeCartLine(key, cartLineDesired[key]);
        }
      });
    });
  }

  function performAddRequest(form) {
    var formData = new FormData(form);
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    var headerEl = document.querySelector('[data-header]');
    var addUrl = (headerEl && headerEl.getAttribute('data-cart-add-url')) || '/cart/add.js';
    return cartRequest(addUrl, { method: 'POST', body: formData });
  }

  function addToCart(form) {
    if (form.dataset.submitting === 'true') return; // guards against double-submit
    form.dataset.submitting = 'true';

    var errorEl = form.querySelector('[data-product-form-error]');
    var submitBtn = form.querySelector('[data-add-to-cart]');

    if (submitBtn) {
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.classList.add('is-loading');
    }
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    var requestGeneration = cartGeneration;

    enqueueCartMutation(function () {
      return performAddRequest(form)
        .then(function (data) {
          if (requestGeneration !== cartGeneration) return; // stale — cart section torn down/reloaded since this started
          applyCartSections(data.sections);
          if (window.AryosCartDrawer) window.AryosCartDrawer.open();
        })
        .catch(function (err) {
          if (requestGeneration !== cartGeneration) return;
          if (errorEl) {
            errorEl.textContent = (err && err.message) || '';
            errorEl.hidden = false;
          }
          // Nothing was added, but resync anyway in case of a partial
          // server-side state change on an edge-case failure — cheap
          // insurance against a stale bubble count.
          resyncCartUI();
        });
    }).finally(function () {
      form.dataset.submitting = 'false';
      if (submitBtn) {
        submitBtn.removeAttribute('aria-busy');
        submitBtn.removeAttribute('disabled');
        submitBtn.classList.remove('is-loading');
      }
    });
  }

  function initAddToCartForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      if (form.dataset.ajaxBound) return;
      form.dataset.ajaxBound = 'true';
      form.addEventListener('submit', function (e) {
        // Progressive enhancement only — if fetch/FormData somehow aren't
        // available, let the form submit normally instead of silently
        // failing.
        if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return;
        e.preventDefault();
        addToCart(form);
      });
    });
  }

  function initCartDrawer() {
    if (cartDrawerTeardown) cartDrawerTeardown();

    var drawer = document.querySelector('[data-cart-drawer]');
    var trigger = document.querySelector('[data-cart-drawer-open]');
    var panel = document.querySelector('[data-cart-drawer-panel]');
    if (!drawer || !panel) return;

    var closeEls = drawer.querySelectorAll('[data-cart-drawer-close]');
    var cleanupFocusTrap = null;
    var lastFocused = null;

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    function open() {
      if (isOpen()) return;
      if (window.AryosHeaderOverlays) {
        window.AryosHeaderOverlays.closeNav();
        window.AryosHeaderOverlays.closeSearch();
      }
      lastFocused = document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
      });
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      cleanupFocusTrap = trapFocusWithin(panel, close);
      window.AryosScrollLock.lock();
    }

    function close() {
      if (!isOpen()) return;
      drawer.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (cleanupFocusTrap) {
        cleanupFocusTrap();
        cleanupFocusTrap = null;
      }
      window.AryosScrollLock.unlock();
      setTimeout(function () {
        drawer.hidden = true;
      }, 300);
      if (lastFocused) lastFocused.focus();
    }

    function onTriggerClick() {
      isOpen() ? close() : open();
    }

    function onKeydown(e) {
      if (e.key === 'Escape' && isOpen()) close();
    }

    if (trigger) trigger.addEventListener('click', onTriggerClick);
    closeEls.forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', onKeydown);

    // Quantity +/- reuse the same generic controller as product pages;
    // the change AJAX below is scoped to this drawer only.
    initQuantitySelectors(drawer);

    function onQuantityChange(e) {
      var input = e.target.closest('[data-cart-quantity-input]');
      if (!input) return;
      var key = input.getAttribute('data-line-key');
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) quantity = 0;
      changeCartLine(key, quantity);
    }

    function onRemoveClick(e) {
      var link = e.target.closest('[data-cart-remove]');
      if (!link) return;
      if (link.hasAttribute('disabled') || link.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }
      if (typeof window.fetch !== 'function') return; // let the native link work
      e.preventDefault();
      changeCartLine(link.getAttribute('data-line-key'), 0);
    }

    drawer.addEventListener('change', onQuantityChange);
    drawer.addEventListener('click', onRemoveClick);

    if (!window.AryosCartDrawer) window.AryosCartDrawer = {};
    window.AryosCartDrawer.open = open;
    window.AryosCartDrawer.close = close;

    cartDrawerTeardown = function teardown() {
      if (trigger) trigger.removeEventListener('click', onTriggerClick);
      closeEls.forEach(function (el) {
        el.removeEventListener('click', close);
      });
      document.removeEventListener('keydown', onKeydown);
      drawer.removeEventListener('change', onQuantityChange);
      drawer.removeEventListener('click', onRemoveClick);
      if (isOpen()) close();
      cartDrawerTeardown = null;
    };
  }

  document.addEventListener('shopify:section:load', function () {
    initAddToCartForms();
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (
      event.target &&
      event.target.querySelector &&
      (event.target.querySelector('[data-cart-drawer]') || event.target.querySelector('[data-cart-drawer-open]'))
    ) {
      initCartDrawer();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (
      event.target &&
      event.target.querySelector &&
      event.target.querySelector('[data-cart-drawer]') &&
      cartDrawerTeardown
    ) {
      cartDrawerTeardown();
      // Drop any queued-but-not-yet-started mutations — nothing left to
      // apply them to. A task already mid-flight can't be un-sent; bumping
      // cartGeneration is what actually protects against it. A fresh DOM
      // query after teardown does NOT reliably find "nothing" — if the
      // Theme Editor immediately re-renders this section
      // (shopify:section:load following the unload), the query would find
      // the *new* instance instead, and an in-flight response arriving
      // after that point would otherwise apply stale mutation output on
      // top of freshly loaded content. Each request captures
      // cartGeneration when it starts and checks it again before applying
      // anything, so a response arriving after this increment is
      // discarded as stale regardless of what the DOM looks like by then.
      cartGeneration++;
      cartMutationQueue = [];
      cartMutationRunning = false;
      cartLineDesired = {};
      cartLineQueued = {};
      cartBusyCount = 0;
    }
  });

  /* ---------------------------------------------------------------------
     Predictive search

     Fetches sections/predictive-search.liquid via Shopify's predictive
     search endpoint using ?section_id=predictive-search, which returns
     real server-rendered HTML (not JSON) — that HTML is inserted as-is
     into the results panel. The user's own query is never used to build
     HTML directly; it only ever goes into the request URL, URL-encoded.

     Accessibility pattern: this uses a *simplified* combobox — the input
     has role="combobox"/aria-expanded/aria-controls, but result rows are
     real <a> links, not role="option" pseudo-controls. Arrow Down/Up move
     real DOM focus between those links (not a synthetic active-descendant
     model). This is deliberate: forcing full ARIA 1.2 combobox semantics
     over linked result cards would mean either fake-linking (bad) or
     hijacking Enter/click behavior on real links (worse). Escape closes
     and returns focus to the input; Tab moves on through the page
     normally — no focus trap.
     --------------------------------------------------------------------- */
  var predictiveSearchTeardowns = {};

  function initPredictiveSearch(scopeId, form) {
    if (predictiveSearchTeardowns[scopeId]) {
      predictiveSearchTeardowns[scopeId]();
    }

    var input = form.querySelector('[data-predictive-search-input]');
    var url = form.getAttribute('data-predictive-search-url');
    var panelContainer = form.closest('[data-search-panel]') || form.parentElement;
    var resultsPanel = panelContainer.querySelector('[data-predictive-search-results]');
    var statusEl = panelContainer.querySelector('[data-predictive-search-status]');
    if (!input || !url || !resultsPanel) return;

    var MIN_QUERY_LENGTH = 2;
    var DEBOUNCE_MS = 300;
    var debounceTimer = null;
    var activeController = null;
    var loadingText = (statusEl && statusEl.getAttribute('data-loading-text')) || '';
    var noResultsText = (statusEl && statusEl.getAttribute('data-no-results-text')) || '';
    var countOneTemplate = (statusEl && statusEl.getAttribute('data-count-one-text')) || '';
    var countOtherTemplate = (statusEl && statusEl.getAttribute('data-count-other-text')) || '';

    function getResultLinks() {
      return Array.prototype.slice.call(resultsPanel.querySelectorAll('a'));
    }

    function openPanel() {
      resultsPanel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function closePanel() {
      resultsPanel.hidden = true;
      resultsPanel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      if (statusEl) statusEl.textContent = '';
    }

    /* ---------------------------------------------------------------------
       Announces a concise result count, not the inserted markup itself.
       resultsPanel intentionally has no aria-live of its own (a live
       region spanning full product cards would read out every title and
       price on every keystroke) — this separate, visually-hidden status
       element is the only thing screen readers hear.

       English-only simplification: Shopify's real `t` filter pluralization
       is used for search.results_count on the (server-rendered) results
       page, but here the count is only known client-side after the fetch
       resolves, so there's no Liquid render to hand it through. This picks
       between two Liquid-rendered templates (one/other) using English's
       binary plural rule. If ARYOS ships additional locales with different
       plural categories later, this specific client-side count check
       would need revisiting — it's a deliberate, narrow trade-off for a
       transient status string, not a general i18n mechanism.
       --------------------------------------------------------------------- */
    function announceResultCount() {
      if (!statusEl) return;
      var count = resultsPanel.querySelectorAll('.predictive-search__item').length;
      if (count === 0) {
        statusEl.textContent = noResultsText;
        return;
      }
      var template = count === 1 ? countOneTemplate : countOtherTemplate;
      statusEl.textContent = template.replace('{{ count }}', String(count));
    }

    function fetchResults(query) {
      if (activeController) activeController.abort();
      var thisController = new AbortController();
      activeController = thisController;

      if (statusEl) statusEl.textContent = loadingText;

      var separator = url.indexOf('?') > -1 ? '&' : '?';
      var requestUrl =
        url +
        separator +
        'q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,article,page,query&resources[limit]=10&section_id=predictive-search';

      fetch(requestUrl, { signal: thisController.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('predictive search request failed');
          return response.text();
        })
        .then(function (html) {
          // A newer request may have started (and become `activeController`)
          // between this fetch resolving and this callback running — if so,
          // this response is stale and must not overwrite the newer state.
          if (activeController !== thisController) return;

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var returnedSection = doc.querySelector('#shopify-section-predictive-search');

          if (!returnedSection) {
            closePanel();
            if (statusEl) statusEl.textContent = noResultsText;
            return;
          }

          resultsPanel.innerHTML = returnedSection.innerHTML;
          openPanel();
          announceResultCount();
        })
        .catch(function (err) {
          if (activeController !== thisController) return;
          if (err && err.name === 'AbortError') return;
          // Network errors fail quietly — the input remains fully usable
          // and Enter still submits a normal full search.
          closePanel();
        });
    }

    function onInput() {
      var query = input.value.trim();
      window.clearTimeout(debounceTimer);

      if (query.length < MIN_QUERY_LENGTH) {
        if (activeController) activeController.abort();
        closePanel();
        return;
      }

      debounceTimer = window.setTimeout(function () {
        fetchResults(query);
      }, DEBOUNCE_MS);
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closePanel();
        input.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        var links = getResultLinks();
        if (!links.length) return;
        e.preventDefault();
        links[0].focus();
      }
    }

    function onResultsKeydown(e) {
      var links = getResultLinks();
      if (!links.length) return;
      var currentIndex = links.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        var next = currentIndex + 1 < links.length ? currentIndex + 1 : 0;
        links[next].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentIndex <= 0) {
          input.focus();
        } else {
          links[currentIndex - 1].focus();
        }
      } else if (e.key === 'Escape') {
        closePanel();
        input.focus();
      }
    }

    function onOutsideClick(e) {
      if (!panelContainer.contains(e.target)) closePanel();
    }

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeydown);
    resultsPanel.addEventListener('keydown', onResultsKeydown);
    document.addEventListener('click', onOutsideClick);

    predictiveSearchTeardowns[scopeId] = function teardown() {
      if (activeController) activeController.abort();
      window.clearTimeout(debounceTimer);
      input.removeEventListener('input', onInput);
      input.removeEventListener('keydown', onKeydown);
      resultsPanel.removeEventListener('keydown', onResultsKeydown);
      document.removeEventListener('click', onOutsideClick);
      closePanel();
      delete predictiveSearchTeardowns[scopeId];
    };

    // Exposed once so header.liquid's own inline script (which owns the
    // outer search-panel open/close) can close predictive results too
    // when the whole search overlay is dismissed, without either script
    // needing to know the other's internals.
    if (!window.AryosPredictiveSearch) window.AryosPredictiveSearch = {};
    window.AryosPredictiveSearch[scopeId] = { close: closePanel };
  }

  /* ---------------------------------------------------------------------
     Collection sorting — auto-submit on change

     The sort form already works with a plain GET submit (a visible submit
     button renders inside <noscript> for that case). This only adds the
     convenience of auto-submitting when JavaScript is available, so nothing
     breaks if it's not.
     --------------------------------------------------------------------- */
  function initCollectionSorting(root) {
    var form = root.querySelector('[data-collection-sort-form]');
    var select = root.querySelector('[data-collection-sort-select]');
    if (!form || !select) return;

    select.addEventListener('change', function () {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Quantity selector
     --------------------------------------------------------------------- */
  function initQuantitySelectors(root) {
    root.querySelectorAll('[data-quantity-selector]').forEach(function (wrapper) {
      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      var minus = wrapper.querySelector('[data-quantity-minus]');
      var plus = wrapper.querySelector('[data-quantity-plus]');
      var parsedMin = parseInt(wrapper.getAttribute('data-min'), 10);
      var min = isNaN(parsedMin) ? 1 : parsedMin;
      var parsedStep = parseInt(wrapper.getAttribute('data-step'), 10);
      var step = isNaN(parsedStep) || parsedStep === 0 ? 1 : parsedStep;
      var maxAttr = wrapper.getAttribute('data-max');
      var max = maxAttr ? parseInt(maxAttr, 10) : null;

      function clamp(value) {
        if (isNaN(value) || value < min) value = min;
        if (max !== null && value > max) value = max;
        return value;
      }

      function setValue(nextValue) {
        input.value = clamp(nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (minus) {
        minus.addEventListener('click', function () {
          setValue((parseInt(input.value, 10) || min) - step);
        });
      }

      if (plus) {
        plus.addEventListener('click', function () {
          setValue((parseInt(input.value, 10) || min) + step);
        });
      }

      input.addEventListener('change', function () {
        input.value = clamp(parseInt(input.value, 10));
      });
    });
  }

  /* ---------------------------------------------------------------------
     Variant-change controller
     --------------------------------------------------------------------- */
  function initVariantPicker(root) {
    var picker = root.querySelector('[data-product-variant-picker]');
    var variantsJsonEl = root.querySelector('[data-product-variants-json]');
    if (!picker || !variantsJsonEl) return;

    var payload;
    try {
      payload = JSON.parse(variantsJsonEl.textContent);
    } catch (err) {
      return;
    }
    var variants = zipVariantPayload(payload);

    var priceEl = root.querySelector('[data-product-price]');
    var unitPriceEl = root.querySelector('[data-unit-price]');
    var variantIdInput = root.querySelector('[data-variant-id-input]');
    var addToCartBtn = root.querySelector('[data-add-to-cart]');
    var addToCartText = root.querySelector('[data-add-to-cart-text]');
    var dynamicCheckout = root.querySelector('[data-dynamic-checkout]');

    var optionKeys = ['option1', 'option2', 'option3'];

    function getSelectedOptions() {
      var values = {};
      picker.querySelectorAll('[data-product-option]').forEach(function (input) {
        var index = input.getAttribute('data-option-index');
        if (input.type === 'radio') {
          if (input.checked) values[index] = input.value;
        } else {
          values[index] = input.value;
        }
      });
      return values;
    }

    function findMatchingVariant(values) {
      var indexes = Object.keys(values);
      if (!indexes.length) return null;
      return (
        variants.filter(function (variant) {
          return indexes.every(function (index) {
            var key = optionKeys[parseInt(index, 10)];
            return variant[key] === values[index];
          });
        })[0] || null
      );
    }

    function updatePrice(variant) {
      if (!priceEl) return;
      if (!variant) return;

      priceEl.innerHTML = '';

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var saleLabel = document.createElement('span');
        saleLabel.className = 'visually-hidden';
        saleLabel.textContent = 'Sale price:';

        var sale = document.createElement('span');
        sale.className = 'product-price__sale';
        sale.setAttribute('data-price', '');
        sale.textContent = variant.formatted_price;

        var compareLabel = document.createElement('span');
        compareLabel.className = 'visually-hidden';
        compareLabel.textContent = 'Regular price:';

        var compare = document.createElement('span');
        compare.className = 'product-price__compare';
        compare.setAttribute('data-compare-price', '');
        compare.textContent = variant.formatted_compare_at_price;

        priceEl.appendChild(saleLabel);
        priceEl.appendChild(sale);
        priceEl.appendChild(compareLabel);
        priceEl.appendChild(compare);
      } else {
        var regular = document.createElement('span');
        regular.className = 'product-price__regular';
        regular.setAttribute('data-price', '');
        regular.textContent = variant.formatted_price;
        priceEl.appendChild(regular);
      }
    }

    function updateUnitPrice(variant) {
      if (!unitPriceEl) return;

      if (!variant || !variant.unit_price_measurement || !variant.formatted_unit_price) {
        unitPriceEl.hidden = true;
        unitPriceEl.textContent = '';
        return;
      }

      var measurement = variant.unit_price_measurement;
      var referencePart =
        measurement.reference_value && measurement.reference_value !== 1
          ? measurement.reference_value
          : '';

      unitPriceEl.hidden = false;
      unitPriceEl.textContent =
        variant.formatted_unit_price + '/' + referencePart + measurement.reference_unit;
    }

    function updateMedia(variant) {
      var mediaId = variant && variant.featured_media_id;
      if (!mediaId) return;
      var mediaEl = root.querySelector('[data-media-id="' + mediaId + '"]');
      if (!mediaEl) return;

      root.querySelectorAll('[data-product-media]').forEach(function (el) {
        el.classList.remove('is-active');
      });
      mediaEl.classList.add('is-active');

      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      mediaEl.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    function updateAddToCart(variant) {
      if (!addToCartBtn) return;

      var labels = {
        addToCart: addToCartBtn.getAttribute('data-label-add-to-cart') || 'Add to Cart',
        soldOut: addToCartBtn.getAttribute('data-label-sold-out') || 'Sold Out',
        unavailable: addToCartBtn.getAttribute('data-label-unavailable') || 'Unavailable'
      };

      if (!variant) {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        if (variantIdInput) variantIdInput.value = '';
        if (addToCartText) addToCartText.textContent = labels.unavailable;
        if (dynamicCheckout) dynamicCheckout.hidden = true;
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;

      if (!variant.available) {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        if (addToCartText) addToCartText.textContent = labels.soldOut;
        if (dynamicCheckout) dynamicCheckout.hidden = true;
      } else {
        addToCartBtn.disabled = false;
        addToCartBtn.removeAttribute('aria-disabled');
        if (addToCartText) addToCartText.textContent = labels.addToCart;
        if (dynamicCheckout) dynamicCheckout.hidden = false;
      }
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, document.title, url.toString());
      } catch (err) {
        /* Silently skip URL updates in unsupported environments */
      }
    }

    function handleChange() {
      var values = getSelectedOptions();
      var variant = findMatchingVariant(values);

      updatePrice(variant);
      updateUnitPrice(variant);
      updateAddToCart(variant);

      if (variant) {
        updateMedia(variant);
        updateUrl(variant);
      }

      root.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { variant: variant },
          bubbles: true
        })
      );
    }

    picker.addEventListener('change', handleChange);
  }

  /* ---------------------------------------------------------------------
     Product recommendations

     sections/product-recommendations.liquid renders twice: once as a
     hidden wrapper on normal page load (carrying the data attributes
     below), and once — fetched here — via Shopify's real recommendations
     endpoint, where `recommendations.products` is genuine server data.
     Only the fetched render's [data-product-recommendations-content]
     inner content is ever extracted and swapped in; the live wrapper's
     own data attributes (real page context) are never touched by that
     swap. The section stays hidden unless the fetched content actually
     contains at least one product card — never an empty heading, never
     placeholder products.

     Uses the same generation-token pattern as the cart controller: a
     response that arrives after this section has been torn down (Theme
     Editor shopify:section:unload) or superseded by a newer fetch is
     discarded rather than applied, regardless of what the DOM looks like
     by the time it resolves.
     --------------------------------------------------------------------- */
  var productRecommendationsGeneration = 0;
  var productRecommendationsTeardowns = {};

  function initProductRecommendations(root) {
    var sectionId = root.getAttribute('data-section-id') || 'product-recommendations';

    if (productRecommendationsTeardowns[sectionId]) {
      productRecommendationsTeardowns[sectionId]();
    }

    var url = root.getAttribute('data-url');
    var productId = root.getAttribute('data-product-id');
    if (!url || !productId) return;

    var limit = root.getAttribute('data-limit');
    var intent = root.getAttribute('data-intent');
    var contentEl = root.querySelector('[data-product-recommendations-content]');
    if (!contentEl) return;

    var thisGeneration = ++productRecommendationsGeneration;
    var abortController = typeof AbortController === 'function' ? new AbortController() : null;

    var params =
      '?section_id=product-recommendations&product_id=' +
      encodeURIComponent(productId) +
      (limit ? '&limit=' + encodeURIComponent(limit) : '') +
      (intent ? '&intent=' + encodeURIComponent(intent) : '');

    fetch(url + params, abortController ? { signal: abortController.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('recommendations request failed');
        return response.text();
      })
      .then(function (html) {
        if (thisGeneration !== productRecommendationsGeneration) return; // superseded or torn down

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var returnedSection = doc.getElementById('shopify-section-product-recommendations');
        if (!returnedSection) return;

        var returnedContent = returnedSection.querySelector('[data-product-recommendations-content]');
        if (!returnedContent) return;

        var hasProducts = returnedContent.querySelector('.product-card') !== null;
        if (!hasProducts) return; // stays hidden — no empty heading, no blank spacing

        contentEl.innerHTML = returnedContent.innerHTML;
        root.hidden = false;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Network or parsing failure — the section simply stays hidden,
        // exactly like the empty-result case. No console errors, no
        // placeholder content on the live storefront.
      });

    productRecommendationsTeardowns[sectionId] = function teardown() {
      if (abortController) abortController.abort();
      productRecommendationsGeneration++; // invalidates this request even if abort() has no effect server-side
      delete productRecommendationsTeardowns[sectionId];
    };
  }

  function initAllProductRecommendations() {
    document.querySelectorAll('[data-product-recommendations]').forEach(initProductRecommendations);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      var root = event.target.querySelector('[data-product-recommendations]');
      if (root) initProductRecommendations(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelector) {
      var root = event.target.querySelector('[data-product-recommendations]');
      var sectionId = root && (root.getAttribute('data-section-id') || 'product-recommendations');
      if (sectionId && productRecommendationsTeardowns[sectionId]) {
        productRecommendationsTeardowns[sectionId]();
      }
    }
  });

  function initHeaderPredictiveSearch() {
    var form = document.querySelector('[data-predictive-search-form]');
    if (form) initPredictiveSearch('header', form);
  }

  function init() {
    document.querySelectorAll('[data-section-type="main-product"]').forEach(function (root) {
      enableVariantIdInput(root);
      initQuantitySelectors(root);
      initVariantPicker(root);
    });

    document.querySelectorAll('[data-section-type="main-collection"]').forEach(function (root) {
      initCollectionSorting(root);
      initFilterDrawer(root);
    });

    initHeaderPredictiveSearch();
    initCartDrawer();
    initAddToCartForms();
    initAllProductRecommendations();
  }

  // The header lives in header-group.json, so Theme Editor edits to it
  // fire shopify:section:load / shopify:section:unload just like any
  // other section — re-initialize or tear down predictive search
  // accordingly rather than leaving stale listeners after a reload.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-predictive-search-form]')) {
      initHeaderPredictiveSearch();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (
      event.target &&
      event.target.querySelector &&
      event.target.querySelector('[data-predictive-search-form]') &&
      predictiveSearchTeardowns.header
    ) {
      predictiveSearchTeardowns.header();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
